/** @format */

/** @format */

import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	InputAdornment,
	OutlinedInput,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
	Grid,
	Container,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Skeleton,
} from "@mui/material";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import BlockIcon from "@mui/icons-material/Block";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { BootstrapDialog } from "../../modal/notification-modal/styles";
import { colors } from "../../../utility/types/constant";
import { SuccessNotification } from "../../allComponents/AllComponents";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	fetchAllCirclesAdmin,
	deleteCircleByIdAdmin,
	banCircleAdmin,
	unbanCircleAdmin,
	fetchCircleByIdAdmin,
} from "../../../redux/actions/circleAction";
import {
	selectAdminCircles,
	selectAdminIsLoading,
	selectAdminError,
} from "../../../redux/selectors/circleSelector";
import { selectCircleData } from "../../../redux/selectors/circleSelector";
import { updateCircleByIdAdmin } from "../../../redux/actions/circleAction";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { ICircleInfo } from "../../../utility/types/circleType";

const AllCirclesAdminView = () => {
	const dispatch = useDispatch<AppDispatch>();
	const circlesData = useSelector(selectAdminCircles);
	const isLoading = useSelector(selectAdminIsLoading);
	const error = useSelector(selectAdminError);

	const [openNav, setOpenNav] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");

	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "ban" | "delete" | null;
		circleId: string | null;
		circleName: string;
	}>({ open: false, type: null, circleId: null, circleName: "" });

	const [processingId, setProcessingId] = useState<string | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);

	// details / edit modal
	const circleDetailFromStore = useSelector(selectCircleData);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editedCircle, setEditedCircle] = useState<Partial<ICircleInfo>>({});

	// members table pagination
	const [membersPage, setMembersPage] = useState(0);
	const [membersRowsPerPage, setMembersRowsPerPage] = useState(4);

	// notification
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});

	// Track initial load to prevent showing error/empty state before first fetch completes
	const [initialLoading, setInitialLoading] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			try {
				await dispatch(fetchAllCirclesAdmin()).unwrap();
			} catch (err) {
				// Error will be handled by selector
			} finally {
				setInitialLoading(false);
			}
		};
		fetchData();
	}, [dispatch]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setPage(0);
	};

	const handleRefresh = async () => {
		try {
			await dispatch(fetchAllCirclesAdmin()).unwrap();
		} catch (err) {
			// ignore, selector will show error
		}
	};

	const handleOpenConfirmDialog = (
		type: "ban" | "delete",
		circleId: string,
		circleName: string,
	) => {
		setConfirmDialog({ open: true, type, circleId, circleName });
	};

	const handleOpenDetails = async (circle: ICircleInfo) => {
		try {
			const res = await dispatch(
				fetchCircleByIdAdmin(circle._id as string),
			).unwrap();
			const payload = (res && (res.circle || res.updatedCircle)) || res || null;
			const detail = payload || circle;
			setEditedCircle(detail as any);
			setDetailsOpen(true);
			setIsEditing(false);
		} catch (err) {
			// ignore
		}
	};

	const handleCloseDetails = () => {
		setDetailsOpen(false);
		setEditedCircle({});
		setIsEditing(false);
		setMembersPage(0);
		// Refresh the circles list to ensure latest data is displayed
		dispatch(fetchAllCirclesAdmin());
	};

	const handleToggleEdit = () => setIsEditing((v) => !v);

	const handleMembersChangePage = (_e: unknown, newPage: number) =>
		setMembersPage(newPage);

	const handleMembersChangeRowsPerPage = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		setMembersRowsPerPage(parseInt(e.target.value, 10));
		setMembersPage(0);
	};

	const handleEditChange = (field: keyof ICircleInfo | string, value: any) => {
		setEditedCircle((prev) => ({ ...prev, [field]: value }));
	};

	const handleSaveCircle = async () => {
		if (!editedCircle || !editedCircle._id) return;
		setIsProcessing(true);
		try {
			const payload: any = {};
			if (editedCircle.circleName) payload.circleName = editedCircle.circleName;
			if (editedCircle.description)
				payload.description = editedCircle.description;

			// membersCount is required by backend validation, always include it
			// Get from editedCircle or fallback to circleDetailFromStore
			const membersCountValue =
				editedCircle.membersCount ?? circleDetailFromStore?.membersCount ?? "";
			payload.membersCount = membersCountValue;

			if (editedCircle.configuration) {
				payload.configuration = {
					...editedCircle.configuration,
					contributionAmount:
						typeof editedCircle.configuration.contributionAmount === "string" ?
							parseFloat(editedCircle.configuration.contributionAmount)
						:	editedCircle.configuration.contributionAmount,
				};
			}

			await dispatch(
				updateCircleByIdAdmin({
					circleId: editedCircle._id as string,
					payload,
				}),
			).unwrap();

			setNotification({
				open: true,
				message: "Circle updated successfully",
				severity: "success",
			});

			await dispatch(fetchAllCirclesAdmin()).unwrap();
			// re-fetch detail
			await dispatch(fetchCircleByIdAdmin(editedCircle._id as string)).unwrap();
			setIsEditing(false);
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || "Failed to update circle",
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
		}
	};

	const handleCloseConfirmDialog = () => {
		if (!isProcessing) {
			setConfirmDialog({
				open: false,
				type: null,
				circleId: null,
				circleName: "",
			});
		}
	};

	const handleConfirmAction = async () => {
		if (!confirmDialog.circleId || !confirmDialog.type) return;
		setIsProcessing(true);
		setProcessingId(confirmDialog.circleId);

		try {
			if (confirmDialog.type === "ban") {
				await dispatch(
					banCircleAdmin({
						circleId: confirmDialog.circleId,
						reason: "Banned by admin",
					}),
				).unwrap();
			} else {
				await dispatch(deleteCircleByIdAdmin(confirmDialog.circleId)).unwrap();
			}

			setNotification({
				open: true,
				message: `Circle ${
					confirmDialog.type === "ban" ? "banned" : "deleted"
				} successfully`,
				severity: "success",
			});

			await dispatch(fetchAllCirclesAdmin()).unwrap();
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || `Failed to ${confirmDialog.type} circle`,
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
			setProcessingId(null);
			handleCloseConfirmDialog();
		}
	};

	const handleToggleBan = async (circle: ICircleInfo) => {
		setIsProcessing(true);
		setProcessingId(circle._id || null);
		try {
			const isBanned = circle && (circle as any).isBanned;
			if (isBanned) {
				await dispatch(unbanCircleAdmin(circle._id as string)).unwrap();
			} else {
				await dispatch(
					banCircleAdmin({
						circleId: circle._id as string,
						reason: "Suspended by admin",
					}),
				).unwrap();
			}

			setNotification({
				open: true,
				message: `Circle ${isBanned ? "unbanned" : "banned"} successfully`,
				severity: "success",
			});

			await dispatch(fetchAllCirclesAdmin()).unwrap();
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || "Failed to update circle ban status",
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
			setProcessingId(null);
		}
	};

	const filtered = (circlesData || []).filter((c) => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return true;
		const nameMatch = c.circleName?.toLowerCase().includes(q);
		const leaderName =
			typeof c.leader === "string" ?
				""
			:	`${(c.leader as any).firstName || ""} ${
					(c.leader as any).lastName || ""
				}`;
		const leaderMatch = leaderName.toLowerCase().includes(q);
		return nameMatch || leaderMatch;
	});

	const paginated = filtered.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(e.target.value, 10));
		setPage(0);
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					<Stack spacing={3}>
						<Stack direction="row" spacing={3}>
							<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">All Circles</Typography>
								<Typography color="text.secondary" variant="body2">
									Manage all circles on the platform
								</Typography>
							</Stack>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<Tooltip title="Refresh">
									<IconButton onClick={handleRefresh} disabled={isLoading}>
										<ArrowsClockwise />
									</IconButton>
								</Tooltip>
							</Box>
						</Stack>

						{/* Stats Cards */}
						<Stack direction="row" spacing={2}>
							<Card sx={{ flex: 1, p: 2 }}>
								{initialLoading ?
									<Skeleton variant="text" width={40} height={32} />
								:	<Typography variant="h6" color="primary">
										{(circlesData || []).length}
									</Typography>
								}
								<Typography variant="body2" color="text.secondary">
									Total Circles
								</Typography>
							</Card>
							<Card sx={{ flex: 1, p: 2 }}>
								{initialLoading ?
									<Skeleton variant="text" width={40} height={32} />
								:	<Typography variant="h6" color="success.main">
										{
											(circlesData || []).filter((c) => !(c as any).isBanned)
												.length
										}
									</Typography>
								}
								<Typography variant="body2" color="text.secondary">
									Active
								</Typography>
							</Card>
							<Card sx={{ flex: 1, p: 2 }}>
								{initialLoading ?
									<Skeleton variant="text" width={40} height={32} />
								:	<Typography variant="h6" color="error.main">
										{
											(circlesData || []).filter((c) => (c as any).isBanned)
												.length
										}
									</Typography>
								}
								<Typography variant="body2" color="text.secondary">
									Banned
								</Typography>
							</Card>
						</Stack>

						{/* Search */}
						<Card>
							<Stack
								direction="row"
								spacing={2}
								sx={{ p: 2 }}
								alignItems="center">
								<OutlinedInput
									value={searchQuery}
									onChange={handleSearchChange}
									fullWidth
									placeholder="Search circles or leader name..."
									startAdornment={
										<InputAdornment position="start">
											<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
										</InputAdornment>
									}
								/>
							</Stack>
						</Card>

						<Card>
							{isLoading || initialLoading ?
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
										gap: 2,
									}}>
									<CircularProgress />
									<Typography variant="body2" color="text.secondary">
										Loading circles...
									</Typography>
								</Box>
							:	<>
									<Box sx={{ overflowX: "auto" }}>
										<Table sx={{ minWidth: 900 }}>
											<TableHead>
												<TableRow>
													<TableCell>Circle</TableCell>
													<TableCell>Leader</TableCell>
													<TableCell>Members</TableCell>
													<TableCell>Contribution</TableCell>
													<TableCell align="center">More Info</TableCell>
													<TableCell align="center">Actions</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{paginated.length === 0 ?
													<TableRow>
														<TableCell colSpan={6} align="center">
															<Typography
																variant="body2"
																color="text.secondary">
																No circles found
															</Typography>
														</TableCell>
													</TableRow>
												:	paginated.map((c: ICircleInfo) => (
														<TableRow hover key={c._id}>
															<TableCell>
																<Stack
																	direction="row"
																	spacing={2}
																	alignItems="center">
																	<Avatar
																		src={c.circleImage as any}
																		sx={{ width: 40, height: 40 }}>
																		{c.circleName?.[0]}
																	</Avatar>
																	<Typography variant="subtitle2">
																		{c.circleName}
																	</Typography>
																</Stack>
															</TableCell>
															<TableCell>
																<Typography>
																	{typeof c.leader === "string" ?
																		c.leader
																	:	`${(c.leader as any).firstName || ""} ${
																			(c.leader as any).lastName || ""
																		}`
																	}
																</Typography>
															</TableCell>
															<TableCell>
																{c.membersCount ||
																	(c.members ? c.members.length : 0)}
															</TableCell>
															<TableCell>
																{c.configuration?.contributionAmount ?? 0}
															</TableCell>
															<TableCell align="center">
																<Tooltip title="More Info">
																	<IconButton
																		size="small"
																		color="primary"
																		onClick={() => handleOpenDetails(c)}>
																		<InfoIcon />
																	</IconButton>
																</Tooltip>
															</TableCell>
															<TableCell>
																<Stack
																	direction="row"
																	spacing={1}
																	justifyContent="center">
																	<Tooltip title="Edit Circle">
																		<IconButton
																			color="primary"
																			size="small"
																			onClick={() => handleOpenDetails(c)}>
																			<EditIcon />
																		</IconButton>
																	</Tooltip>
																	<Tooltip
																		title={
																			(c as any).isBanned ?
																				"Unban Circle"
																			:	"Ban Circle"
																		}>
																		<IconButton
																			color="warning"
																			size="small"
																			onClick={() => handleToggleBan(c)}
																			disabled={
																				isProcessing && processingId === c._id
																			}>
																			{isProcessing && processingId === c._id ?
																				<CircularProgress size={18} />
																			:	<BlockIcon />}
																		</IconButton>
																	</Tooltip>
																	<Tooltip title="Delete Circle">
																		<IconButton
																			color="error"
																			size="small"
																			onClick={() =>
																				handleOpenConfirmDialog(
																					"delete",
																					c._id as string,
																					c.circleName,
																				)
																			}
																			disabled={
																				isProcessing && processingId === c._id
																			}>
																			{isProcessing && processingId === c._id ?
																				<CircularProgress size={18} />
																			:	<DeleteIcon />}
																		</IconButton>
																	</Tooltip>
																</Stack>
															</TableCell>
														</TableRow>
													))
												}
											</TableBody>
										</Table>
									</Box>
									<Divider />
									<TablePagination
										component="div"
										count={filtered.length}
										page={page}
										onPageChange={handleChangePage}
										rowsPerPage={rowsPerPage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										rowsPerPageOptions={[5, 10, 25, 50]}
									/>
								</>
							}
						</Card>
					</Stack>
				</Main>
			</Box>

			<Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
				<DialogTitle>
					{confirmDialog.type === "ban" ? "Ban Circle" : "Delete Circle"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to {confirmDialog.type} the circle "
						{confirmDialog.circleName}"? This action may be irreversible.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseConfirmDialog} startIcon={<InfoIcon />}>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmAction}
						color="error"
						variant="contained">
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			{/* Circle Details / Edit Modal */}
			<BootstrapDialog
				open={detailsOpen}
				onClose={handleCloseDetails}
				fullWidth
				maxWidth="lg">
				<DialogContent dividers sx={{ fontSize: "1.1rem" }}>
					<IconButton
						aria-label="close"
						onClick={handleCloseDetails}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: colors.white,
							backgroundColor: colors.cancel,
							"&:hover": {
								color: colors.white,
								backgroundColor: colors.cancel,
							},
						}}>
						<CloseIcon />
					</IconButton>
					<Box
						component="main"
						sx={{
							flexGrow: 1,
							py: 1,
							justifyContent: "center",
							display: "flex",
							textAlign: "center",
							alignItems: "center",
						}}>
						<Container maxWidth="lg" sx={{ mt: 1, my: 2 }}>
							<Stack spacing={8} sx={{ width: "100%" }}>
								<div>
									<Grid justifyContent="center" container spacing={5}>
										<Grid item xs={12} md={10} lg={10}>
											<Stack spacing={3}>
												<Stack
													direction="row"
													justifyContent="space-between"
													alignItems="center">
													<Typography variant="h5">
														{isEditing ?
															"Edit Circle Details"
														:	"Circle Details"}
													</Typography>
													{!isEditing && (
														<Button
															variant="outlined"
															startIcon={<EditIcon />}
															onClick={handleToggleEdit}>
															Edit
														</Button>
													)}
												</Stack>{" "}
												<Stack direction="row" spacing={3} alignItems="center">
													<Avatar
														src={
															(circleDetailFromStore as any)?.circleImage as any
														}
														sx={{ width: 80, height: 80 }}>
														{(circleDetailFromStore as any)?.circleName?.[0] ||
															(editedCircle.circleName || "")[0]}
													</Avatar>
													<Box>
														<Typography variant="h6">
															{circleDetailFromStore?.circleName ||
																editedCircle.circleName}
														</Typography>
														<Typography variant="body2" color="text.secondary">
															Created:{" "}
															{circleDetailFromStore?.created_At ?
																new Date(circleDetailFromStore.created_At)
																	.toISOString()
																	.split("T")[0]
															:	""}
														</Typography>
													</Box>
												</Stack>
												<Grid container spacing={3}>
													<Grid item xs={12} sm={6}>
														<TextField
															label="Circle Name"
															fullWidth
															value={
																isEditing ?
																	editedCircle.circleName || ""
																:	circleDetailFromStore?.circleName || ""
															}
															onChange={(e) =>
																handleEditChange("circleName", e.target.value)
															}
															disabled={!isEditing}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<FormControl fullWidth disabled={!isEditing}>
															<InputLabel id="membersCount-label">
																Members Count
															</InputLabel>
															<Select
																labelId="membersCount-label"
																id="membersCount-select"
																value={
																	isEditing ?
																		(editedCircle.membersCount as any) || ""
																	:	circleDetailFromStore?.membersCount || ""
																}
																label="Members Count"
																onChange={(e) =>
																	handleEditChange(
																		"membersCount",
																		e.target.value,
																	)
																}>
																<MenuItem value="<=5">
																	Less than or equal to 5
																</MenuItem>
																<MenuItem value=">5 <10">
																	Greater than 5 and less than 10
																</MenuItem>
																<MenuItem value=">=10">
																	Greater than or equal to 10
																</MenuItem>
															</Select>
														</FormControl>
													</Grid>{" "}
													<Grid item xs={12}>
														<TextField
															label="Description"
															fullWidth
															multiline
															minRows={3}
															value={
																isEditing ?
																	editedCircle.description || ""
																:	circleDetailFromStore?.description || ""
															}
															onChange={(e) =>
																handleEditChange("description", e.target.value)
															}
															disabled={!isEditing}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<TextField
															label="Contribution Amount"
															type="number"
															fullWidth
															value={
																isEditing ?
																	(editedCircle.configuration
																		?.contributionAmount as any) || ""
																:	circleDetailFromStore?.configuration
																		?.contributionAmount || ""
															}
															onChange={(e) =>
																handleEditChange("configuration", {
																	...(editedCircle.configuration || {}),
																	contributionAmount: e.target.value,
																})
															}
															disabled={!isEditing}
														/>
													</Grid>{" "}
													<Grid item xs={12} sm={6}>
														<TextField
															label="Actual Members"
															fullWidth
															value={
																circleDetailFromStore?.members?.length ?? "N/A"
															}
															disabled
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<FormControl fullWidth disabled={!isEditing}>
															<InputLabel id="contributionPeriod-label">
																Contribution Period
															</InputLabel>
															<Select
																labelId="contributionPeriod-label"
																value={
																	isEditing ?
																		(editedCircle.configuration
																			?.contributionPeriod as any) || ""
																	:	circleDetailFromStore?.configuration
																			?.contributionPeriod || ""
																}
																label="Contribution Period"
																onChange={(e) =>
																	handleEditChange("configuration", {
																		...(editedCircle.configuration || {}),
																		contributionPeriod: e.target.value,
																	})
																}>
																<MenuItem value="Weekly">Weekly</MenuItem>
																<MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs={12} sm={6}>
														<TextField
															label="Contribution Frequency"
															type="number"
															fullWidth
															value={
																isEditing ?
																	(editedCircle.configuration
																		?.contributionFrequency as any) || ""
																:	circleDetailFromStore?.configuration
																		?.contributionFrequency || ""
															}
															onChange={(e) =>
																handleEditChange("configuration", {
																	...(editedCircle.configuration || {}),
																	contributionFrequency: e.target.value,
																})
															}
															disabled={!isEditing}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<FormControl fullWidth disabled={!isEditing}>
															<InputLabel id="contributionDays-label">
																Contribution Days
															</InputLabel>
															<Select
																labelId="contributionDays-label"
																multiple
																value={
																	isEditing ?
																		(
																			Array.isArray(
																				editedCircle.configuration
																					?.contributionDays,
																			)
																		) ?
																			editedCircle.configuration
																				?.contributionDays || []
																		:	[]
																	: (
																		Array.isArray(
																			circleDetailFromStore?.configuration
																				?.contributionDays,
																		)
																	) ?
																		circleDetailFromStore?.configuration
																			?.contributionDays || []
																	:	[]
																}
																label="Contribution Days"
																onChange={(e) =>
																	handleEditChange("configuration", {
																		...(editedCircle.configuration || {}),
																		contributionDays: e.target.value,
																	})
																}
																renderValue={(selected) =>
																	(selected as string[]).join(", ")
																}>
																<MenuItem value="Monday">Monday</MenuItem>
																<MenuItem value="Tuesday">Tuesday</MenuItem>
																<MenuItem value="Wednesday">Wednesday</MenuItem>
																<MenuItem value="Thursday">Thursday</MenuItem>
																<MenuItem value="Friday">Friday</MenuItem>
																<MenuItem value="Saturday">Saturday</MenuItem>
																<MenuItem value="Sunday">Sunday</MenuItem>
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs={12} sm={6}>
														<TextField
															label="Max Contribution Time"
															fullWidth
															value={
																isEditing ?
																	(editedCircle.configuration
																		?.maxContributionTime as any) || ""
																:	circleDetailFromStore?.configuration
																		?.maxContributionTime || ""
															}
															onChange={(e) =>
																handleEditChange("configuration", {
																	...(editedCircle.configuration || {}),
																	maxContributionTime: e.target.value,
																})
															}
															disabled={!isEditing}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<FormControl fullWidth disabled={!isEditing}>
															<InputLabel id="payOutPeriod-label">
																PayOut Period
															</InputLabel>
															<Select
																labelId="payOutPeriod-label"
																value={
																	isEditing ?
																		(editedCircle.configuration
																			?.payOutPeriod as any) || "Monthly"
																	:	circleDetailFromStore?.configuration
																			?.payOutPeriod || "Monthly"
																}
																label="PayOut Period"
																onChange={(e) =>
																	handleEditChange("configuration", {
																		...(editedCircle.configuration || {}),
																		payOutPeriod: e.target.value,
																	})
																}>
																<MenuItem value="Monthly">Monthly</MenuItem>
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs={12} sm={6}>
														<FormControl fullWidth disabled={!isEditing}>
															<InputLabel id="payOutDay-label">
																PayOut Day
															</InputLabel>
															<Select
																labelId="payOutDay-label"
																value={
																	isEditing ?
																		(editedCircle.configuration
																			?.payOutDay as any) || ""
																	:	circleDetailFromStore?.configuration
																			?.payOutDay || ""
																}
																label="PayOut Day"
																onChange={(e) =>
																	handleEditChange("configuration", {
																		...(editedCircle.configuration || {}),
																		payOutDay: e.target.value,
																	})
																}>
																<MenuItem value="Monday">Monday</MenuItem>
																<MenuItem value="Tuesday">Tuesday</MenuItem>
																<MenuItem value="Wednesday">Wednesday</MenuItem>
																<MenuItem value="Thursday">Thursday</MenuItem>
																<MenuItem value="Friday">Friday</MenuItem>
															</Select>
														</FormControl>
													</Grid>
												</Grid>
												{isEditing && (
													<Stack
														direction="row"
														spacing={2}
														justifyContent="flex-end">
														<Button
															variant="outlined"
															onClick={() => setIsEditing(false)}
															disabled={isProcessing}>
															Cancel
														</Button>
														<Button
															variant="contained"
															onClick={handleSaveCircle}
															disabled={isProcessing}>
															{isProcessing ?
																<CircularProgress size={20} />
															:	"Save Changes"}
														</Button>
													</Stack>
												)}
											</Stack>
										</Grid>
									</Grid>
								</div>
							</Stack>

							{/* Members Table */}
							<Box sx={{ mt: 4 }}>
								<Divider sx={{ mb: 2 }} />
								<Typography variant="h6" sx={{ mb: 2 }}>
									Circle Members
								</Typography>
								<Card>
									<Box sx={{ overflowX: "auto" }}>
										<Table>
											<TableHead>
												<TableRow>
													<TableCell>Profile</TableCell>
													<TableCell>Full Name</TableCell>
													<TableCell>Email</TableCell>
													<TableCell>Status</TableCell>
													<TableCell>Joined Date</TableCell>
													<TableCell align="center">Verified</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(
													circleDetailFromStore?.members &&
													(circleDetailFromStore.members as any[]).length > 0
												) ?
													(circleDetailFromStore.members as any[])
														.slice(
															membersPage * membersRowsPerPage,
															membersPage * membersRowsPerPage +
																membersRowsPerPage,
														)
														.map((member: any, index: number) => {
															const memberData = member.memberId || member;
															const status =
																memberData.isDeleted ? "Deleted"
																: memberData.isBanned ? "Banned"
																: memberData.isSuspended ? "Suspended"
																: "Active";

															return (
																<TableRow key={index}>
																	<TableCell>
																		<Avatar
																			src={memberData.profileImage}
																			sx={{ width: 40, height: 40 }}>
																			{memberData.firstName?.[0] || "U"}
																		</Avatar>
																	</TableCell>
																	<TableCell>
																		<Typography variant="body2">
																			{`${memberData.firstName || ""} ${
																				memberData.lastName || ""
																			}`}
																		</Typography>
																	</TableCell>
																	<TableCell>
																		<Typography variant="body2">
																			{memberData.email || "N/A"}
																		</Typography>
																	</TableCell>
																	<TableCell>
																		<Typography
																			variant="body2"
																			sx={{
																				color:
																					status === "Active" ? "success.main"
																					: status === "Banned" ? "error.main"
																					: status === "Suspended" ?
																						"warning.main"
																					:	"text.secondary",
																				fontWeight: 500,
																			}}>
																			{status}
																		</Typography>
																	</TableCell>
																	<TableCell>
																		<Typography variant="body2">
																			{member.joinedAt ?
																				new Date(
																					member.joinedAt,
																				).toLocaleDateString()
																			:	"N/A"}
																		</Typography>
																	</TableCell>
																	<TableCell align="center">
																		<Typography
																			variant="body2"
																			sx={{
																				color:
																					memberData.isVerified ? "success.main"
																					:	"text.secondary",
																			}}>
																			{memberData.isVerified ? "Yes" : "No"}
																		</Typography>
																	</TableCell>
																</TableRow>
															);
														})
												:	<TableRow>
														<TableCell colSpan={6} align="center">
															<Typography
																variant="body2"
																color="text.secondary">
																No members found
															</Typography>
														</TableCell>
													</TableRow>
												}
											</TableBody>
										</Table>
									</Box>
									<TablePagination
										component="div"
										count={
											(circleDetailFromStore?.members as any[])?.length || 0
										}
										page={membersPage}
										onPageChange={handleMembersChangePage}
										rowsPerPage={membersRowsPerPage}
										onRowsPerPageChange={handleMembersChangeRowsPerPage}
										rowsPerPageOptions={[4, 8, 12]}
									/>
								</Card>
							</Box>
						</Container>
					</Box>
				</DialogContent>
			</BootstrapDialog>

			{/* Success/Error Notification */}
			<SuccessNotification
				open={notification.open}
				onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
				message={notification.message}
				severity={notification.severity}
			/>
		</React.Fragment>
	);
};

export default AllCirclesAdminView;
